import { filter } from 'ramda';
import { useMemo } from 'react';
import type { MKEERAApiResponseItem } from '../dashboard.types';
import { formatDistrict, parseAmount } from '../utils/dataProcessing';

interface UsePayeeTypeChartDataParams {
  data: MKEERAApiResponseItem[];
  selectedDistrict?: string;
}

export function usePayeeTypeChartData({ data, selectedDistrict }: UsePayeeTypeChartDataParams) {
  return useMemo(() => {
    // Safety check: ensure data is an array
    if (!Array.isArray(data) || data.length === 0) {
      return { entries: [] };
    }

    // Filter data by selected district
    const filteredData = selectedDistrict
      ? filter((item: MKEERAApiResponseItem) => formatDistrict(item.applicationData.aldermanicDistrict) === selectedDistrict, data)
      : data;

    const counts: Record<string, number> = {};
    const sums: Record<string, number> = {};
    filteredData.forEach((item) => {
      const key = item.applicationData.payeeType?.trim() || 'Unknown';
      counts[key] = (counts[key] || 0) + 1;
      sums[key] = (sums[key] || 0) + parseAmount(item.applicationData.amountOfPayment);
    });

    const totalAmount = Object.values(sums).reduce((a, b) => a + b, 0);
    const labels = Object.keys(counts);
    const entries = labels
      .map((label) => ({
        label,
        value: sums[label],
        percent: totalAmount ? Math.round((sums[label] / totalAmount) * 1000) / 10 : 0, // 1 decimal
        count: counts[label],
        formatted: sums[label].toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }),
      }))
      .sort((a, b) => b.value - a.value);
    return { entries };
  }, [data, selectedDistrict]);
}
